"use client";

import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const screenings = [{
  number: "01",
  name: "Pest Control",
  description: "Dependable treatment for common household pests.",
  detail: "Our signature defense protocol targets ants, roaches, and spiders at their source. We utilize eco-conscious, professional-grade barriers to ensure your living space remains a sanctuary."
}, {
  number: "02",
  name: "Mosquito Control",
  description: "Strategic barrier treatments for outdoor enjoyment.",
  detail: "We deploy precision misting technology and larval growth regulators to disrupt the mosquito life cycle, allowing you to reclaim your yard without the nuisance of bites."
}, {
  number: "03",
  name: "Entry Point Treatments",
  description: "Targeted treatments around doors, windows, and entry points, plus minor sealing of small cracks and holes.",
  detail: "We apply targeted treatments around doors, windows, garage doors, utility and pipe penetrations, vents, eaves, and other exterior entry points, and we seal small cracks and small holes, to help keep pests from getting inside your home."
}, {
  number: "04",
  name: "Rodent Control",
  description: "Targeted exterior treatment and minor exclusion to help keep mice and rats away from your home.",
  detail: "Our rodent control protocol combines exterior perimeter treatments, targeted entry point treatments, and minor exclusion of small cracks and holes, with monitoring and follow-up as your plan requires."
}];
export default function ComprehensivePestSolutions() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="3384" id="comprehensive-pest-solutions" className="bg-background text-foreground py-24 md:py-32 lg:py-40">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        
        <div ref={headerRef} className={`max-w-3xl transition-all duration-1000 ease-out ${headerVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          <div className="mb-8 flex items-center gap-4">
            <span className="block h-px w-10 bg-primary" aria-hidden="true" />
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Elite Defense — 04
            </span>
          </div>

          <h2 className="font-default text-2xl sm:text-3xl lg:text-4xl leading-[1.05] tracking-tight text-foreground sm:text-5xl md:text-6xl lg:text-7xl">
            Four precise layers of <span className="font-serif italic text-primary">protection</span>.
          </h2>
        </div>

        
        <div className="mt-20 md:mt-28 border-t border-border">
          <Accordion type="single" collapsible className="w-full">
            {screenings.map((service, i) => {
            const {
              ref,
              isVisible
            } = useRevealOnScroll<HTMLDivElement>({
              threshold: 0.15
            });
            return <div data-index={i} ref={ref} key={service.number} className={`group/row border-b border-border transition-all duration-700 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`} style={{
              transitionDelay: `${i * 80}ms`
            }}>
                  <AccordionItem value={service.number} className="border-b-0">
                    <AccordionTrigger className="group/trigger w-full px-2 py-6 sm:py-12 md:py-16 transition-colors duration-500 hover:no-underline hover:bg-accent/60 rounded-md [&>svg]:hidden">
                      <div className="grid w-full md:grid-cols-12 items-center gap-4 md:gap-8 text-left">
                        
                        <div className="col-span-2 md:col-span-1">
                          <span className="font-serif italic text-2xl md:text-4xl text-primary">
                            {service.number}
                          </span>
                        </div>

                        
                        <div className="col-span-10 md:col-span-5">
                          <h3 className="font-default text-2xl md:text-3xl lg:text-4xl tracking-tight text-foreground transition-colors duration-500 group-hover/trigger:text-primary">
                            {service.name}
                          </h3>
                        </div>

                        
                        <div className="col-span-10 col-start-3 md:col-span-5 md:col-start-auto">
                          <p className="text-sm md:text-base text-muted-foreground leading-relaxed font-default">
                            {service.description}
                          </p>
                        </div>

                        
                        <div className="col-span-12 md:col-span-1 flex justify-end">
                          <span className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border text-foreground transition-all duration-500 group-hover/trigger:border-primary group-hover/trigger:text-primary group-data-[state=open]/trigger:rotate-45">
                            <Plus className="h-4 w-4" strokeWidth={1.5} />
                          </span>
                        </div>
                      </div>
                    </AccordionTrigger>

                    <AccordionContent className="px-2 pb-12 md:pb-16">
                      <div className="grid grid-cols-12 gap-4 md:gap-8">
                        <div className="w-full md:col-span-6 md:col-start-7">
                          <p className="font-serif text-lg md:text-xl leading-relaxed text-foreground/80 mb-6">
                            {service.detail}
                          </p>
                          <Link to="/ContactPage" className="inline-flex items-center gap-2 text-sm text-primary transition-colors hover:text-foreground">
                            <span className="font-mono uppercase tracking-[0.15em] text-xs">
                              Request a quote
                            </span>
                            <span className="block h-px w-6 bg-primary transition-all group-hover:w-10" aria-hidden="true" />
                          </Link>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </div>;
          })}
          </Accordion>
        </div>

        
        <div className="mt-20 md:mt-28 flex justify-center">
          <Button asChild variant="outline" size="lg" className="rounded-md border border-primary bg-transparent px-10 py-6 text-foreground hover:bg-primary hover:text-primary-foreground transition-colors duration-500">
            <Link to="/ServicesPage">
              <span className="font-mono text-xs uppercase tracking-[0.2em]">
                Explore All Solutions
              </span>
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}